// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/first-sync-gate/status-throttle.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/shared/src/powersync/status-throttle.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo malla-vpn-app
//     node fleet/fleet.ts check --repo malla-vpn-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * Throttle for PowerSync status listeners that always delivers the LATEST value.
 *
 * The status listeners used to throttle like this:
 *
 *   statusChanged: (s) => {
 *     if (timeout) return;                       // drops every later status
 *     timeout = setTimeout(() => publish(s), ms); // publishes the FIRST one
 *   }
 *
 * That publishes the first status of each window, late, and throws away every
 * status that arrives while the window is open. The UI therefore always lags
 * one step behind, and when the LAST status of a burst (download finished,
 * `last_synced_at` written) lands inside an open window it is lost for good:
 * nothing else is published until PowerSync happens to emit again. The
 * first-sync card then sits on a stale percentage and its state machine never
 * hears that the sync completed.
 *
 * This keeps the same rate limit (at most one publish per window) but each
 * publish carries the most recent value received.
 *
 * PHASES (`options.phaseOf`). "Latest value wins" is right for progress ticks
 * and wrong across a phase change: a whole first sync of a few hundred
 * operations can go connecting → downloading → synced inside ONE window, and
 * then only `synced` was published. The first-sync state machine jumped from
 * `connecting` straight to `synced` and the determinate progress bar never
 * mounted — measured in CI (run 36282298504: `connecting` at
 * 5200 ms, `synced` at 5409 ms, no `downloading` in between). With `phaseOf`,
 * two values of different phases are never merged: pushing a value whose phase
 * differs from the pending one publishes the pending value at once and starts
 * a new window for the new one, so every phase is on screen for at least one
 * window. Values within a phase are still coalesced, and without `phaseOf` the
 * behaviour is exactly the plain latest-value throttle.
 */
export interface LatestThrottle<T> {
  /** Records `value`; it (or a newer value) is published within `ms`. */
  push: (value: T) => void;
  /** Drops any pending publish. Call it on cleanup. */
  cancel: () => void;
}

export interface LatestThrottleOptions<T> {
  /** Values whose phases differ are never coalesced into one publish. */
  phaseOf?: (value: T) => string;
}

export function createLatestThrottle<T>(
  publish: (value: T) => void,
  ms: number,
  options: LatestThrottleOptions<T> = {},
): LatestThrottle<T> {
  const { phaseOf } = options;
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let pending: { value: T } | null = null;

  const openWindow = () => {
    timeout = setTimeout(() => {
      timeout = null;
      const latest = pending;
      pending = null;
      if (latest) {
        publish(latest.value);
      }
    }, ms);
  };

  return {
    push(value) {
      if (pending && phaseOf && phaseOf(pending.value) !== phaseOf(value)) {
        // Phase boundary: the pending value is the last of its phase. Publish it
        // now instead of letting `value` overwrite it, and give `value` a full
        // window so the flushed phase stays visible for at least `ms`.
        if (timeout) {
          clearTimeout(timeout);
        }
        const flushed = pending.value;
        pending = { value };
        openWindow();
        publish(flushed);
        return;
      }
      pending = { value };
      if (timeout) {
        return;
      }
      openWindow();
    },
    cancel() {
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = null;
      pending = null;
    },
  };
}
